// ═══════════════════════════════════════════════════════════════
// DonorDetailView — Shared Sub-Components
// Path: src/components/donorDetail/DonorDetailShared.jsx
//
// Pure presentational components extracted from DonorDetailView.jsx
// during modularization Phase 1. No state, no context, no handlers.
//
// Exports:
//   Field, SectionHead, AccentSection, CollapsibleSection,
//   OptOutBadge, getDaysColor
// ═══════════════════════════════════════════════════════════════

import React, { useState } from "react";
import {
  ChevronDown,
  Edit3,
  Save,
  ShieldAlert,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { FormInput, FormSelect } from "../ui/FormControls";

// ═══════════════════════════════════════════════════════════════
// getDaysColor — engagement days-since-contact color
// ═══════════════════════════════════════════════════════════════

export const getDaysColor = (days) => {
  if (days === null) return "#dc2626";
  if (days <= 14) return "#16a34a";
  if (days <= 30) return "#65a30d";
  if (days <= 60) return "#d97706";
  return "#dc2626";
};

// ═══════════════════════════════════════════════════════════════
// Field — labeled form field (text, select, read-only)
// ═══════════════════════════════════════════════════════════════

export const Field = ({
  label,
  field,
  type,
  options,
  editing,
  detailForm,
  updateField,
  palette,
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <span
      style={{
        fontSize: FONT_SIZES.xs,
        fontWeight: 600,
        color: palette.text2,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {label}
    </span>
    {editing ? (
      options ? (
        <FormSelect
          value={detailForm[field] || ""}
          onChange={(v) => updateField(field, v)}
          options={options}
          placeholder="Select..."
        />
      ) : (
        <FormInput
          value={detailForm[field] || ""}
          onChange={(v) => updateField(field, v)}
          type={type}
        />
      )
    ) : (
      <span
        style={{
          fontSize: FONT_SIZES.md,
          color: detailForm[field] ? palette.text1 : palette.text3,
        }}
      >
        {detailForm[field] || "\u2014"}
      </span>
    )}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// AccentSection — colored-header card (used in Classic layout)
// ═══════════════════════════════════════════════════════════════

export const AccentSection = ({ title, icon: SI, color, children }) => (
  <div
    style={{
      marginBottom: 16,
      borderRadius: 12,
      overflow: "hidden",
      border: `2px solid ${color}`,
      boxShadow: `0 4px 16px rgba(0,0,0,0.1), 0 0 0 1px ${color}40`,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "12px 20px",
        background: color,
      }}
    >
      <SI size={ICON_SIZES.md} style={{ color: "#ffffff" }} />
      <h3
        style={{
          margin: 0,
          fontSize: FONT_SIZES.md,
          fontWeight: 700,
          color: "#ffffff",
          fontFamily: FONT_FAMILY,
        }}
      >
        {title}
      </h3>
    </div>
    <div style={{ padding: 20, background: "#ffffff" }}>{children}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// CollapsibleSection — expandable card (Classic layout lower sections)
// ═══════════════════════════════════════════════════════════════

export const CollapsibleSection = ({
  title,
  icon: SI,
  children,
  muted = false,
  palette,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  return (
    <div
      style={{
        marginBottom: 12,
        borderRadius: 12,
        border: `1px solid ${palette.cardBorder}`,
        background: palette.cardBg,
        opacity: muted ? (collapsed ? 0.6 : 0.8) : collapsed ? 0.75 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          cursor: "pointer",
          borderBottom: collapsed ? "none" : `1px solid ${palette.cardBorder}`,
          borderRadius: collapsed ? 12 : "12px 12px 0 0",
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = `${palette.accent1}06`)
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SI
            size={ICON_SIZES.sm}
            style={{ color: muted ? palette.text3 : palette.accent1 }}
          />
          <h3
            style={{
              margin: 0,
              fontSize: FONT_SIZES.sm,
              fontWeight: 600,
              color: muted ? palette.text2 : palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            {title}
          </h3>
        </div>
        <ChevronDown
          size={ICON_SIZES.sm}
          style={{
            color: palette.text3,
            transition: "transform 0.2s",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
          }}
        />
      </div>
      {!collapsed && <div style={{ padding: 20 }}>{children}</div>}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SectionHead — colored bar header for 2-column main sections
// ═══════════════════════════════════════════════════════════════

export const SectionHead = ({
  title,
  palette,
  icon: SI,
  accentColor,
  editingThis,
  onEdit,
  onSave,
  onCancel,
  collapsible,
  collapsed,
  onToggle,
  canEdit: sectionCanEdit,
}) => {
  const bgColor = accentColor || palette.accent1;
  return (
    <div
      onClick={collapsible && !editingThis ? onToggle : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        marginBottom: editingThis ? 0 : 12,
        userSelect: "none",
        background: bgColor,
        borderRadius: editingThis ? "8px 8px 0 0" : 8,
        transition: "all 0.15s ease",
        boxShadow: `0 2px 8px ${bgColor}30`,
        cursor: collapsible && !editingThis ? "pointer" : "default",
      }}
      onMouseEnter={(e) => {
        if (collapsible && !editingThis)
          e.currentTarget.style.filter = "brightness(1.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = "none";
      }}
    >
      {SI && <SI size={ICON_SIZES.md} style={{ color: "#ffffff", flexShrink: 0 }} />}
      <span
        style={{
          fontSize: FONT_SIZES.md,
          fontWeight: 800,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#ffffff",
          fontFamily: FONT_FAMILY,
          flex: 1,
        }}
      >
        {title}
      </span>
      {collapsible && !editingThis && (
        <ChevronDown
          size={ICON_SIZES.sm}
          style={{
            color: "rgba(255,255,255,0.7)",
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            flexShrink: 0,
          }}
        />
      )}
      {editingThis && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: bgColor,
            background: "#ffffff",
            padding: "2px 8px",
            borderRadius: 4,
          }}
        >
          EDITING
        </span>
      )}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", alignItems: "center", gap: 6 }}
      >
        {editingThis ? (
          <>
            <button
              onClick={onSave}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "rgba(255,255,255,0.95)",
                border: "none",
                cursor: "pointer",
                color: bgColor,
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: FONT_SIZES.xs,
                fontWeight: 700,
                fontFamily: FONT_FAMILY,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.95)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <Save size={ICON_SIZES.xs} /> Save
            </button>
            <button
              onClick={onCancel}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: "rgba(255,255,255,0.2)",
                border: "none",
                cursor: "pointer",
                color: "#ffffff",
                padding: "5px 10px",
                borderRadius: 6,
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          sectionCanEdit &&
          onEdit && (
            <button
              onClick={onEdit}
              title="Edit this section"
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                cursor: "pointer",
                color: "#ffffff",
                padding: "4px 8px",
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.2)";
              }}
            >
              <Edit3 size={ICON_SIZES.sm} /> Edit
            </button>
          )
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// OptOutBadge — small inline indicator for contact preference opt-outs
// v1.3.0 — Contact Preference Codes
// ═══════════════════════════════════════════════════════════════

export const OptOutBadge = ({ label }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      fontSize: 9,
      fontWeight: 700,
      color: "#b45309",
      background: "#fef3c7",
      border: "1px solid #fde68a",
      padding: "1px 6px",
      borderRadius: 4,
      marginLeft: 6,
      whiteSpace: "nowrap",
    }}
  >
    <ShieldAlert size={ICON_SIZES.xs} />
    {label}
  </span>
);